import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../api/axios';
import { toast } from 'react-toastify';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import {
    CalendarDaysIcon, GlobeAmericasIcon, MapPinIcon, UserIcon
} from '@heroicons/react/24/outline';
import {
    ClockIcon, CheckCircleIcon as CheckSolid, XCircleIcon, BoltIcon, UsersIcon
} from '@heroicons/react/24/solid';

const localizer = momentLocalizer(moment);

// --- 1. DATE FIXER (Timezone Agnostic) ---
const toLocalDate = (dateStr) => {
    if (!dateStr) return new Date();
    return new Date(dateStr);
};

// --- 2. SHORT CODE MAPPER ---
const getLeaveShortCode = (name, reason = '') => {
    const n = name.toLowerCase();
    const r = (reason || '').toLowerCase();

    if (r.includes('half day') || r.includes('half-day')) return 'HD';
    if (r.includes('permission')) return 'PERM';

    if (n.includes('casual')) return 'CL';
    if (n.includes('sick')) return 'SL';
    if (n.includes('earned') || n.includes('privilege')) return 'EL';
    if (n.includes('maternity')) return 'ML';
    if (n.includes('paternity')) return 'PL';
    if (n.includes('comp') || n.includes('off')) return 'CO';
    if (n.includes('loss') || n.includes('unpaid')) return 'LOP';

    return 'L';
};

// --- 3. COLOR MAPPING ---
const getLeaveStyle = (name, reason = '') => {
    const n = name.toLowerCase();
    const r = (reason || '').toLowerCase();

    if (r.includes('half day') || r.includes('half-day')) return { bg: '#f59e0b' }; // Amber
    if (r.includes('permission')) return { bg: '#06b6d4' }; // Cyan

    if (n.includes('sick')) return { bg: '#f43f5e' };        // Rose
    if (n.includes('casual')) return { bg: '#f97316' };    // Orange
    if (n.includes('earned') || n.includes('privilege')) return { bg: '#059669' }; // Emerald
    if (n.includes('maternity') || n.includes('paternity')) return { bg: '#d946ef' }; // Fuchsia
    if (n.includes('comp') || n.includes('off')) return { bg: '#6366f1' }; // Indigo

    return { bg: '#3b82f6' }; // Default Blue
};

// --- 4. CUSTOM EVENT BAR (UPDATED DISPLAY) ---
const CustomEvent = ({ event }) => {
    const isHoliday = event.resource.type === 'holiday';
    const isGlobal = event.resource.country === 'Global';

    const titleLower = event.title.toLowerCase();
    const isPermission = titleLower.includes('permission');

    // 1. HOLIDAY DISPLAY
    if (isHoliday) {
        return (
            <div className={`flex items-center space-x-1 text-[10px] font-bold px-1 py-0.5 rounded ${isGlobal ? 'bg-purple-600 text-white' : 'bg-slate-500 text-white'}`}>
                {isGlobal ? <GlobeAmericasIcon className="h-3 w-3" /> : <MapPinIcon className="h-3 w-3" />}
                <span className="truncate">{event.title}</span>
            </div>
        );
    }

    // 2. LEAVE DISPLAY (Name - ShortCode - Days)
    const shortCode = event.resource.is_unpaid ? 'LOP' : getLeaveShortCode(event.resource.leave_name, event.resource.reason);
    const displayName = event.resource.employee_name ? event.resource.employee_name.split(' ')[0] : 'Me';
    const daysCount = event.resource.days_count; // Get the day count

    return (
        <div className="flex items-center justify-between text-[10px] sm:text-xs font-bold text-white overflow-hidden px-1 w-full h-full">
            <div className="flex items-center space-x-1 truncate">
                {isPermission && <BoltIcon className="h-3 w-3 text-white" />}
                <span className="truncate">
                    {/* UPDATED FORMAT: Name - Code - Days */}
                    {displayName} - {shortCode} - {daysCount}
                </span>
            </div>
            <div className="flex items-center space-x-0.5 flex-shrink-0">
                {event.resource.status === 'Pending' && <ClockIcon className="h-3 w-3 opacity-90" />}
                {event.resource.status === 'Approved' && <CheckSolid className="h-3 w-3 opacity-90" />}
            </div>
        </div>
    );
};

const CustomToolbar = ({ label, view, views, onNavigate, onView }) => {
    return (
        <div className="flex flex-col sm:flex-row justify-between items-center mb-6 p-3 bg-slate-50 rounded-xl shadow-sm border border-slate-100 gap-4">
            <div className="flex space-x-2">
                <button className="bg-white text-slate-700 px-4 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-100 text-sm font-bold shadow-sm" onClick={() => onNavigate('PREV')}>Back</button>
                <button className="bg-white text-slate-700 px-4 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-100 text-sm font-bold shadow-sm" onClick={() => onNavigate('NEXT')}>Next</button>
                <button className="bg-white text-indigo-600 px-4 py-1.5 rounded-lg border border-indigo-200 hover:bg-indigo-50 text-sm font-bold shadow-sm" onClick={() => onNavigate('TODAY')}>Today</button>
            </div>
            <span className="text-xl font-black text-slate-800 tracking-tight">{label}</span>
            <div className="flex bg-white p-1 rounded-xl border border-slate-200 shadow-sm">
                {views.map(v => (
                    <button
                        key={v} onClick={() => onView(v)}
                        className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${view === v ? 'bg-slate-900 text-white shadow-md' : 'text-slate-500 hover:bg-slate-50'}`}
                    >
                        {v.charAt(0).toUpperCase() + v.slice(1)}
                    </button>
                ))}
            </div>
        </div>
    );
};

const LeaveCalendar = () => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionModal, setActionModal] = useState({ isOpen: false, event: null });
    const [date, setDate] = useState(new Date());
    const [view, setView] = useState('month');

    const fetchCalendarData = async () => {
        try {
            const res = await api.get('/leaves/calendar');
            const calendarEvents = [];

            // 1. Process Holidays
            res.data.holidays.forEach(h => {
                const rawDate = toLocalDate(h.holiday_date);
                calendarEvents.push({
                    id: `h-${h.id}`,
                    title: h.name,
                    start: rawDate,
                    end: rawDate,
                    allDay: true,
                    resource: { type: 'holiday', country: h.country }
                });
            });

            // 2. Process Leaves
            res.data.myLeaves.forEach(l => {
                const startDate = toLocalDate(l.start_date);
                const endDateRaw = toLocalDate(l.end_date);

                const reasonLower = (l.reason || '').toLowerCase();
                let effectiveTitle = l.leave_name;
                if (reasonLower.includes('half day')) effectiveTitle = `Half Day (${l.leave_name})`;
                else if (reasonLower.includes('permission')) effectiveTitle = `Permission (${l.leave_name})`;

                const styleInfo = getLeaveStyle(l.leave_name, l.reason);

                let endDate = endDateRaw;
                if (startDate.getTime() !== endDateRaw.getTime()) {
                    endDate = moment(endDateRaw).add(1, 'days').toDate();
                }

                calendarEvents.push({
                    id: `l-${l.id}`,
                    title: effectiveTitle,
                    start: startDate,
                    end: endDate,
                    allDay: true,
                    resource: {
                        ...l,
                        type: 'leave',
                        bgColor: styleInfo.bg
                    }
                });
            });

            setEvents(calendarEvents);
            setLoading(false);
        } catch (err) {
            toast.error("Failed to load calendar");
            setLoading(false);
        }
    };

    useEffect(() => { fetchCalendarData(); }, []);

    // --- 5. DETAILED TOOLTIP ---
    const eventPropGetter = (event) => {
        const isHoliday = event.resource.type === 'holiday';
        let bgColor = '#3b82f6';
        if (isHoliday) {
            bgColor = event.resource.country === 'Global' ? '#9333ea' : '#64748b';
        } else {
            bgColor = event.resource.bgColor;
        }

        let tooltip = "";
        if (isHoliday) {
            tooltip = `${event.title} (${event.resource.country})`;
        } else {
            // Full details on hover
            tooltip = `Employee: ${event.resource.employee_name || 'N/A'}\n` +
                `Type: ${event.title}\n` +
                `Duration: ${event.resource.days_count} Day(s)\n` +
                `Status: ${event.resource.status}\n` +
                `Approver: ${event.resource.approver_name || 'Pending'}`;
        }

        return {
            style: {
                backgroundColor: bgColor,
                borderRadius: '6px',
                border: 'none',
                color: 'white',
                display: 'block'
            },
            title: tooltip
        };
    };

    if (loading) return <Layout><div className="flex justify-center h-[80vh] items-center text-slate-400 font-bold animate-pulse">Loading Calendar...</div></Layout>;

    return (
        <Layout>
            <div className="mb-8">
                <h1 className="text-3xl font-black text-slate-800 tracking-tight flex items-center">
                    <CalendarDaysIcon className="h-9 w-9 mr-3 text-indigo-600" />
                    Company Calendar
                </h1>
            </div>

            {/* LEGEND - UPDATED WITH SHORT CODES */}
            <div className="flex flex-wrap gap-4 mb-8 px-6 py-4 bg-white rounded-2xl border border-slate-100 shadow-sm">
                <div className="flex items-center text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                    <span className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: '#f59e0b' }}></span> HD (Half Day)
                </div>
                <div className="flex items-center text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                    <span className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: '#06b6d4' }}></span> PERM (Permission)
                </div>
                <div className="flex items-center text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                    <span className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: '#059669' }}></span> EL (Earned)
                </div>
                <div className="flex items-center text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                    <span className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: '#f43f5e' }}></span> SL (Sick)
                </div>
                <div className="flex items-center text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                    <span className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: '#f97316' }}></span> CL (Casual)
                </div>
                <div className="flex items-center text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                    <span className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: '#9333ea' }}></span> Global Holiday
                </div>
            </div>

            <div className="bg-white p-6 rounded-[2.5rem] shadow-2xl border border-slate-100">
                <Calendar
                    localizer={localizer}
                    events={events}
                    style={{ height: 800 }}
                    onSelectEvent={(e) => e.resource.type === 'leave' && setActionModal({ isOpen: true, event: e })}
                    components={{ event: CustomEvent, toolbar: CustomToolbar }}
                    eventPropGetter={eventPropGetter}
                    date={date}
                    view={view}
                    onNavigate={setDate}
                    onView={setView}
                    popup={true}
                />
            </div>

            {/* MODAL */}
            {actionModal.isOpen && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-sm p-8 border border-white/20 animate-fade-in-up">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-black text-slate-800">Leave Details</h2>
                            <button onClick={() => setActionModal({ isOpen: false, event: null })} className="text-slate-400 hover:text-slate-900"><XCircleIcon className="h-8 w-8" /></button>
                        </div>
                        <div className="space-y-4">

                            {/* 1. Name */}
                            <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 flex items-center">
                                <UserIcon className="w-5 h-5 text-indigo-500 mr-3" />
                                <div>
                                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Employee</p>
                                    <p className="font-bold text-slate-800">{actionModal.event.resource.employee_name}</p>
                                </div>
                            </div>

                            {/* 2. Leave Type */}
                            <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mb-1">Leave Type</p>
                                <p className="font-bold text-slate-800 text-lg">{actionModal.event.title}</p>
                            </div>

                            <div className="flex gap-4">
                                {/* 3. No of Days */}
                                <div className="flex-1 p-3 bg-slate-50 rounded-xl border border-slate-100">
                                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mb-1">Duration</p>
                                    <p className="font-bold text-slate-800">{actionModal.event.resource.days_count} Day(s)</p>
                                </div>

                                {/* 4. Status Tag */}
                                <div className="flex-1 p-3 bg-slate-50 rounded-xl border border-slate-100 flex items-center justify-center">
                                    <span className={`px-3 py-1 rounded-full text-[10px] font-bold ${actionModal.event.resource.status === 'Approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                        {actionModal.event.resource.status}
                                    </span>
                                </div>
                            </div>

                            {/* 5. Approved By (L1/L2) */}
                            <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                                <div className="flex items-center mb-1">
                                    <UsersIcon className="w-4 h-4 text-slate-400 mr-1.5" />
                                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                                        {actionModal.event.resource.status === 'Approved' ? 'Approved By' : 'Pending With'}
                                    </p>
                                </div>
                                <p className="font-bold text-slate-800">
                                    {actionModal.event.resource.approver_name || 'System / Admin'}
                                    {/* Show Level Hint */}
                                    <span className="text-xs text-slate-400 font-medium ml-2">
                                        (Level {actionModal.event.resource.current_level})
                                    </span>
                                </p>
                            </div>

                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
};

export default LeaveCalendar;